import { defaultBrand } from '@sunucupark/design-tokens';
import defaultBrandRolesJson from '@sunucupark/design-tokens/brand/default-roles.json';
import type { BrandColorRoles } from '@sunucupark/shared';
import appleTouchIcon from '@sunucupark/design-tokens/assets/brand/apple-touch-icon.png?url';
import favicon from '@sunucupark/design-tokens/assets/brand/favicon.svg?url';
import icon192 from '@sunucupark/design-tokens/assets/brand/icon-192.png?url';
import icon512 from '@sunucupark/design-tokens/assets/brand/icon-512.png?url';
import mark from '@sunucupark/design-tokens/assets/brand/mark.svg?url';
import maskable512 from '@sunucupark/design-tokens/assets/brand/maskable-512.png?url';
import wordmarkDark from '@sunucupark/design-tokens/assets/brand/wordmark-dark.svg?url';
import wordmarkLight from '@sunucupark/design-tokens/assets/brand/wordmark-light.svg?url';

/**
 * Everything the build or the server decides. The gateway URL is relative by
 * default: nginx serves the app and proxies `/api` to the gateway on the same
 * origin, so the refresh cookie stays `SameSite=Strict` (ADR 0007).
 */
/**
 * The packaged brand roles. The design-tokens build generates the file from the
 * palette and a test there fails when it drifts; the gateway validates the same
 * file with zod before serving it, so the browser can trust its shape without
 * pulling a validator into the first download.
 */
const defaultBrandRoles = defaultBrandRolesJson as BrandColorRoles;

/**
 * Share of page views that report their Core Web Vitals (GAP H1). A tenth in
 * production is plenty for a histogram and costs one beacon per ten loads;
 * development measures nothing unless it is asked to.
 */
const vitalsSampleRate = Number(
  import.meta.env.VITE_VITALS_SAMPLE ?? (import.meta.env.PROD ? '0.1' : '0'),
);

export const config = {
  gatewayUrl: import.meta.env.VITE_GATEWAY_URL ?? '',
  version: import.meta.env.VITE_APP_VERSION ?? '0.1.0',
  platform: 'web',
  vitalsSampleRate: Number.isFinite(vitalsSampleRate) ? Math.min(Math.max(vitalsSampleRate, 0), 1) : 0,
  /**
   * The brand packaged with this build. It is shown only when `GET /branding`
   * cannot be reached at startup, so a user never faces a blank page; the
   * gateway remains the source of the brand (ADR 0009).
   */
  brand: defaultBrand,
  brandRoles: defaultBrandRoles,
  brandAssets: {
    mark,
    wordmarkLight,
    wordmarkDark,
    favicon,
    icon192,
    icon512,
    appleTouchIcon,
    maskable512,
  },
} as const;
